import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AdminWrapper from "../../../components/AdminWrapper";
import { BASE_URL } from "../../../config/config";
import { PERMISSIONS, userCan } from "../../../config/adminRoles";

// Owners ask OTS to release their share of a tenant's online payment (once the
// hold period is over) or to pay out their wallet. Finance/manager gets a
// claim ready; only the super admin releases the money — a release is final,
// and a tenant who cancels afterwards gets nothing back.

const TABS = [
  { key: "pending", label: "New claims" },
  { key: "ready", label: "Ready to release" },
  { key: "approved", label: "Released" },
  { key: "rejected", label: "Declined" },
  { key: "void", label: "Voided" },
];

const CHIP = {
  pending: { bg: "#fef3c7", color: "#92400e", label: "New" },
  ready: { bg: "#dbeafe", color: "#1e40af", label: "Ready" },
  approved: { bg: "#dcfce7", color: "#166534", label: "Released" },
  rejected: { bg: "#fee2e2", color: "#991b1b", label: "Declined" },
  void: { bg: "#e5e7eb", color: "#374151", label: "Voided" },
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
const when = (d) => (d ? moment(d).format("DD MMM YYYY, h:mm A") : "—");

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token")}`,
});

const Field = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Grid>
);

export default function OwnerPayouts() {
  const open = useSelector((state) => state.sidebar.open);
  const currentUser = useSelector((state) => state.validateUser?.UserValidate?.data?.user);
  const canApprove = userCan(currentUser, PERMISSIONS.APPROVE_PAYOUTS);

  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [totalWaiting, setTotalWaiting] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(null); // null | "release" | "reject"
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/admin/payouts`, {
        params: { status },
        headers: authHeaders(),
      });
      setRows(data.data || []);
      setCounts(data.counts || {});
      setTotalWaiting(data.totalWaiting || 0);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "We could not load the payout claims. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const close = () => {
    if (busy) return;
    setSelected(null);
    setMode(null);
    setText("");
  };

  const act = async (path, body, success) => {
    setBusy(true);
    try {
      await axios.post(`${BASE_URL}/admin/payouts/${selected.id}/${path}`, body, { headers: authHeaders() });
      toast.success(success);
      setSelected(null);
      setMode(null);
      setText("");
      load(tab);
    } catch (err) {
      toast.error(err.response?.data?.message || "We could not complete that action. Please try again.");
      load(tab);
    } finally {
      setBusy(false);
    }
  };

  const markReady = async () => {
    const r = await Swal.fire({
      title: "Mark this claim as ready?",
      text: `Confirm that you have checked ${selected.ownerName}'s bank details and that the transfer of ${money(selected.amount)} is prepared for the super admin to release.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, ready",
    });
    if (r.isConfirmed) act("ready", {}, "The claim is ready for the super admin to release.");
  };

  const release = async () => {
    if (text.trim().length < 3) return toast.error("Please enter the bank transfer reference.");
    const r = await Swal.fire({
      title: `Release ${money(selected.amount)}?`,
      html: "This is final and cannot be undone. Please confirm that you have transferred the money and entered the correct reference.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, release",
      confirmButtonColor: "#16a34a",
    });
    if (r.isConfirmed) act("approve", { reference: text.trim() }, "The payment has been released to the owner.");
  };

  const reject = () => {
    if (text.trim().length < 3) return toast.error("Please give the owner a reason.");
    act("reject", { reason: text.trim() }, "The claim was declined and the owner has been notified.");
  };

  const isBooking = selected?.kind === "booking";
  const actionable = selected && ["pending", "ready"].includes(selected.status);

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Owner Payouts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Owners claim their share of a tenant's online payment once the hold period is over. OTS's
          commission is never part of a payout.
          {!canApprove && " Finance prepares the claim; the super admin releases the money."}
        </Typography>

        <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e5e7eb", borderRadius: 2, display: "inline-block" }}>
          <Typography variant="caption" color="text.secondary">
            Waiting to be released
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {money(totalWaiting)}
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ mb: 2, borderRadius: 2 }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: "1px solid #e5e7eb", "& .MuiTab-root": { textTransform: "none", fontWeight: 500 } }}
          >
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={`${t.label} (${counts[t.key] ?? 0})`} />
            ))}
          </Tabs>
        </Paper>

        <Paper sx={{ borderRadius: 2, overflowX: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary">Nothing here.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Owner</TableCell>
                  <TableCell>For</TableCell>
                  <TableCell align="right">Amount to release</TableCell>
                  <TableCell>Asked</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => setSelected(r)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {r.ownerName || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.ownerPhone || r.ownerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.kind === "booking" ? (
                        <>
                          {r.flatName}
                          <Typography variant="caption" color="text.secondary" display="block">
                            Tenant: {r.tenantName}
                          </Typography>
                        </>
                      ) : (
                        "Wallet balance"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{money(r.amount)}</strong>
                    </TableCell>
                    <TableCell>{moment(r.requestedAt).fromNow()}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={CHIP[r.status].label}
                        sx={{ bgcolor: CHIP[r.status].bg, color: CHIP[r.status].color, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small">Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Dialog open={Boolean(selected)} onClose={close} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              {isBooking ? "Booking payout" : "Wallet payout"} — {selected.ownerName}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                {money(selected.amount)}
              </Typography>

              {isBooking && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Field label="Property" value={selected.flatName} />
                    <Field label="Tenant" value={selected.tenantName} />
                    <Field label="Tenant paid (total)" value={money(selected.paidAmount)} />
                    <Field label="OTS commission (kept)" value={money(selected.commissionAmount)} />
                    <Field label="Gateway fee (kept)" value={money(selected.processingAmount)} />
                    <Field label="Owner's share (advance)" value={money(selected.amount)} />
                  </Grid>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Only the advance is released. If the tenant cancels before this is released, the claim
                    is voided automatically and the refund rules apply.
                  </Alert>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Pay to
              </Typography>
              {selected.bank && selected.bank.accountNumber ? (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Field label="Bank" value={selected.bank.bankName} />
                  <Field label="Account title" value={selected.bank.accountName} />
                  <Field label="Account number" value={selected.bank.accountNumber} />
                  <Field label="IBAN" value={selected.bank.ibanNumber} />
                  <Field label="Phone" value={selected.ownerPhone} />
                </Grid>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This owner has not added bank details. Get them before releasing.
                </Alert>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                <Field label="Asked" value={when(selected.requestedAt)} />
                {selected.readyAt && <Field label={`Prepared by ${selected.readyByName}`} value={when(selected.readyAt)} />}
                {selected.decidedAt && (
                  <Field label={`Decided by ${selected.decidedByName}`} value={when(selected.decidedAt)} />
                )}
                {selected.reference && <Field label="Transfer reference" value={selected.reference} />}
                {selected.reason && <Field label="Reason" value={selected.reason} />}
              </Grid>

              {actionable && mode && (
                <TextField
                  fullWidth
                  autoFocus
                  sx={{ mt: 2 }}
                  label={mode === "release" ? "Bank transfer reference" : "Reason (the owner will see this)"}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  multiline={mode === "reject"}
                  rows={mode === "reject" ? 2 : 1}
                />
              )}
              {actionable && !canApprove && selected.status === "ready" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Ready. Waiting for the super admin to release it.
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={close} disabled={busy}>
                Close
              </Button>
              {actionable && !mode && (
                <>
                  <Button color="error" onClick={() => setMode("reject")} disabled={busy}>
                    Decline
                  </Button>
                  {selected.status === "pending" && (
                    <Button
                      variant={canApprove ? "outlined" : "contained"}
                      onClick={markReady}
                      disabled={busy}
                      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {busy ? "Saving…" : "Mark ready"}
                    </Button>
                  )}
                  {canApprove && (
                    <Button variant="contained" color="success" onClick={() => setMode("release")} disabled={busy}>
                      Release…
                    </Button>
                  )}
                </>
              )}
              {actionable && mode && (
                <>
                  <Button
                    onClick={() => {
                      setMode(null);
                      setText("");
                    }}
                    disabled={busy}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    color={mode === "release" ? "success" : "error"}
                    onClick={mode === "release" ? release : reject}
                    disabled={busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {busy ? (mode === "release" ? "Releasing payment…" : "Declining claim…") : mode === "release" ? "Release payment" : "Decline claim"}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      <ToastContainer position="top-right" />
    </>
  );
}
